// ============================================================================
// GOLD RATE HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { goldRateService } from 'api/services/goldRateService';
import type { GoldRate, CreateGoldRateRequest, UpdateGoldRateRequest } from 'api/types/goldRates.types';
import type { ID } from 'api/types';
import { notifyDataChanged, useDataChangeListener } from './useDataSync';

const DATA_KEY = 'gold-rates';

export const useGoldRates = () => {
  const [data, setData] = useState<GoldRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchGoldRates = useCallback(async (): Promise<GoldRate[]> => {
    setLoading(true);
    setError(null);
    try {
      const response = await goldRateService.getAll({ pageSize: 50000 });
      setData(response.data);
      return response.data;
    } catch (err) {
      setError(err as Error);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGoldRates();
  }, [fetchGoldRates]);

  useDataChangeListener(DATA_KEY, fetchGoldRates);

  return { data, loading, error, refetch: fetchGoldRates };
};

export const useCreateGoldRate = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createGoldRate = async (data: CreateGoldRateRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await goldRateService.create(data);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { createGoldRate, loading, error };
};

export const useUpdateGoldRate = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateGoldRate = async (id: ID, data: UpdateGoldRateRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await goldRateService.update(id, data);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { updateGoldRate, loading, error };
};

export const useDeleteGoldRate = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteGoldRate = async (id: ID) => {
    setLoading(true);
    setError(null);
    try {
      await goldRateService.delete(id);
      notifyDataChanged(DATA_KEY);
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { deleteGoldRate, loading, error };
};

export const useActivateGoldRate = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const activateGoldRate = async (id: ID) => {
    setLoading(true);
    setError(null);
    try {
      const response = await goldRateService.activate(id);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { activateGoldRate, loading, error };
};
